import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== repeat) return toast.error("De wachtwoorden komen niet overeen");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/auth");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </span>
          <span className="font-heading font-extrabold text-xl tracking-tight">VoltRoute</span>
        </Link>
        <div className="bg-card rounded-2xl p-8 shadow-[0_8px_32px_rgba(31,38,59,0.1)]">
          <p className="font-heading font-bold text-lg flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-primary" /> Nieuw wachtwoord instellen</p>
          <p className="text-sm text-muted-foreground mb-6">Kies een nieuw wachtwoord voor je account.</p>
          {!token ? (
            <div data-testid="reset-no-token">
              <p className="text-sm text-muted-foreground mb-4">Deze link is onvolledig. Vraag een nieuwe herstel-link aan via de inlogpagina.</p>
              <Link to="/auth" className="text-sm font-semibold text-primary hover:underline">Naar inloggen</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nieuw wachtwoord (min. 6 tekens)</Label>
                <Input id="new-password" data-testid="reset-password-input" type="password" required minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeat-password">Herhaal wachtwoord</Label>
                <Input id="repeat-password" data-testid="reset-password-repeat-input" type="password" required minLength={6} value={repeat}
                  onChange={(e) => setRepeat(e.target.value)} placeholder="••••••••" />
              </div>
              <Button data-testid="reset-password-submit" type="submit" disabled={loading}
                className="w-full rounded-full bg-primary hover:bg-[#1B4332] active:scale-95 transition-[transform,background-color] duration-200">
                {loading ? "Bezig..." : "Wachtwoord wijzigen"}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
